import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from './config';
import { detectUserCurrency } from '../services/currencyService';
import { clearErrorLogs } from './database';

// Function to translate Firebase error codes to user-friendly messages
const getAuthErrorMessage = (errorCode) => {
  switch (errorCode) {
    case 'auth/invalid-credential':
      return 'Invalid email or password. Please check your credentials and try again.';
    case 'auth/user-not-found':
      return 'No account found with this email address.';
    case 'auth/wrong-password':
      return 'Incorrect password. Please try again.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/user-disabled':
      return 'This account has been disabled. Please contact support.';
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please try again later.';
    case 'auth/email-already-in-use':
      return 'An account with this email already exists.';
    case 'auth/weak-password':
      return 'Password is too weak. Please choose a stronger password with at least 6 characters.';
    case 'auth/operation-not-allowed':
      return 'This sign-in method is not enabled.';
    case 'auth/network-request-failed':
      return 'Network error. Please check your connection and try again.';
    case 'auth/requires-recent-login':
      return 'This operation requires recent authentication. Please log in again.';
    case 'auth/credential-already-in-use':
      return 'This credential is already associated with a different account.';
    case 'auth/invalid-verification-code':
      return 'Invalid verification code. Please try again.';
    case 'auth/invalid-verification-id':
      return 'Invalid verification ID. Please try again.';
    case 'auth/missing-email':
      return 'Email address is required.';
    case 'auth/missing-password':
      return 'Password is required.';
    case 'auth/quota-exceeded':
      return 'Too many requests. Please try again later.';
    case 'auth/timeout':
      return 'Request timed out. Please try again.';
    default:
      return 'An error occurred. Please try again.';
  }
};

// Function to ensure user document exists and is up to date
const ensureUserDocument = async (user) => {
  try {
    const userDocRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userDocRef);
    
    if (!userDoc.exists()) {
      // Create user document if it doesn't exist
      const detectedCurrency = await detectUserCurrency();
      await setDoc(userDocRef, {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        createdAt: new Date(),
        preferences: {
          currency: detectedCurrency.code,
          location: null
        }
      });
      console.log('Created missing user document for', user.uid);
    } else {
      // Update existing user document with current auth data
      const userData = userDoc.data();
      const updates = {};
      
      if (user.email !== userData.email) {
        updates.email = user.email;
      }
      if (user.displayName !== userData.displayName) {
        updates.displayName = user.displayName;
      }
      
      if (Object.keys(updates).length > 0) {
        await updateDoc(userDocRef, updates);
        console.log('Updated user document for', user.uid);
      }
    }
  } catch (error) {
    console.error('Error ensuring user document:', error);
  }
};

export const signUp = async (email, password, displayName) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Update the user's display name
    await updateProfile(user, { displayName });
    
    // Detect user's currency based on location
    const detectedCurrency = await detectUserCurrency();
    
    // Create user document in Firestore
    await setDoc(doc(db, 'users', user.uid), {
      uid: user.uid,
      email: user.email,
      displayName: displayName,
      createdAt: new Date(),
      preferences: {
        currency: detectedCurrency.code,
        location: null
      }
    });
    
    return { success: true, user };
  } catch (error) {
    const friendlyMessage = getAuthErrorMessage(error.code);
    return { success: false, error: friendlyMessage };
  }
};

export const signIn = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Ensure user document exists and is up to date
    await ensureUserDocument(user);
    
    return { success: true, user };
  } catch (error) {
    const friendlyMessage = getAuthErrorMessage(error.code);
    return { success: false, error: friendlyMessage };
  }
};

export const logout = async () => {
  try {
    await signOut(auth);
    // Clear error logs when user logs out
    clearErrorLogs();
    return { success: true };
  } catch (error) {
    const friendlyMessage = getAuthErrorMessage(error.code);
    return { success: false, error: friendlyMessage };
  }
};

export const getCurrentUser = () => {
  return auth.currentUser;
};

export const onAuthStateChange = (callback) => {
  return onAuthStateChanged(auth, callback);
};

export const getUserData = async (uid) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', uid));
    if (userDoc.exists()) {
      return { success: true, data: userDoc.data() };
    } else {
      return { success: false, error: 'User not found' };
    }
  } catch (error) {
    const friendlyMessage = getAuthErrorMessage(error.code);
    return { success: false, error: friendlyMessage };
  }
};

export const sendPasswordResetEmail = async (email) => {
  try {
    await firebaseSendPasswordResetEmail(auth, email);
    return { success: true };
  } catch (error) {
    const friendlyMessage = getAuthErrorMessage(error.code);
    return { success: false, error: friendlyMessage };
  }
};
